import React, { useState } from "react";
import { Form, Button, Modal, Checkbox, Message, Icon, Loader } from "semantic-ui-react";
import { determineEnergyUnits } from "../../../../utils/dataAndFormulas/energyForms";
import { useLanguage } from "../../../../contexts";
import "./UpdateUnitsModal.scss";

export function UpdateUnitsModal(props) {
  const { show, onClose, onApply, currentUnits, registeredFields, isLoading } = props;
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;

  // Estado para campos seleccionados y sus unidades
  const [selectedFields, setSelectedFields] = useState({});
  const [fieldUnits, setFieldUnits] = useState({});

  // Lista de todos los campos de energía
  const allEnergyFields = [
    { key: "electricity_standard_combined", label: t("electricity_standard_combined") },
    { key: "electricity_standard_domestic", label: t("electricity_standard_domestic") },
    { key: "electricity_standard_production", label: t("electricity_standard_production") },
    { key: "electricity_cost_total", label: t("electricity_cost_total") },
    { key: "renewable_energies_ppa_combined", label: t("renewable_energies_ppa_combined") },
    { key: "renewable_energies_ppa_domestic", label: t("renewable_energies_ppa_domestic") },
    { key: "renewable_energies_ppa_production", label: t("renewable_energies_ppa_production") },
    { key: "renewable_energies_ppa_cost", label: t("renewable_energies_ppa_cost") },
    { key: "renewable_energies_on_site_combined", label: t("renewable_energies_on_site_combined") },
    { key: "renewable_energies_on_site_domestic", label: t("renewable_energies_on_site_domestic") },
    { key: "renewable_energies_on_site_production", label: t("renewable_energies_on_site_production") },
    { key: "renewable_energies_on_site_maintenance_and_amortization", label: t("renewable_energies_on_site_maintenance_and_amortization") },
    { key: "natural_gas_combined", label: t("natural_gas_combined") },
    { key: "natural_gas_cogeneration", label: t("natural_gas_cogeneration") },
    { key: "natural_gas_production_operations", label: t("natural_gas_production_operations") },
    { key: "natural_gas_domestic", label: t("natural_gas_domestic") },
    { key: "natural_gas_total_cost", label: t("natural_gas_total_cost") },
    { key: "glp_combined", label: t("glp_combined") },
    { key: "glp_cogeneration", label: t("glp_cogeneration") },
    { key: "glp_production_operations", label: t("glp_production_operations") },
    { key: "glp_domestic", label: t("glp_domestic") },
    { key: "glp_total_cost", label: t("glp_total_cost") },
    { key: "heavy_fuel_oil_total", label: t("heavy_fuel_oil_total") },
    { key: "heavy_fuel_oil_total_cost", label: t("heavy_fuel_oil_total_cost") },
    { key: "light_fuel_oil_total", label: t("light_fuel_oil_total") },
    { key: "light_fuel_oil_total_cost", label: t("light_fuel_oil_total_cost") },
    { key: "coal_total", label: t("coal_total") },
    { key: "coal_total_cost", label: t("coal_total_cost") },
    { key: "diesel_combined", label: t("diesel_combined") },
    { key: "diesel_cogeneration", label: t("diesel_cogeneration") },
    { key: "diesel_production_operations", label: t("diesel_production_operations") },
    { key: "diesel_forklifts", label: t("diesel_forklifts") },
    { key: "diesel_total_cost", label: t("diesel_total_cost") },
    { key: "biodiesel_combined", label: t("biodiesel_combined") },
    { key: "biodiesel_cogeneration", label: t("biodiesel_cogeneration") },
    { key: "biodiesel_production_operations", label: t("biodiesel_production_operations") },
    { key: "biodiesel_forklifts", label: t("biodiesel_forklifts") },
    { key: "biodiesel_total_cost", label: t("biodiesel_total_cost") },
    { key: "gasoline_combined", label: t("gasoline_combined") },
    { key: "gasoline_cogeneration", label: t("gasoline_cogeneration") },
    { key: "gasoline_production_operations", label: t("gasoline_production_operations") },
    { key: "gasoline_forklifts", label: t("gasoline_forklifts") },
    { key: "gasoline_total_cost", label: t("gasoline_total_cost") },
    { key: "biofuel_combined", label: t("biofuel_combined") },
    { key: "biofuel_cogeneration", label: t("biofuel_cogeneration") },
    { key: "biofuel_production_operations", label: t("biofuel_production_operations") },
    { key: "biofuel_forklifts", label: t("biofuel_forklifts") },
    { key: "biofuel_total_cost", label: t("biofuel_total_cost") },
    { key: "biomass_total", label: t("biomass_total") },
    { key: "biomass_total_cost", label: t("biomass_total_cost") },
    { key: "steam_total", label: t("steam_total") },
    { key: "steam_total_cost", label: t("steam_total_cost") },
  ];

  // Filtrar campos según registeredFields si existen
  const energyFields =
    registeredFields && registeredFields.length > 0
      ? registeredFields
          .filter((field) => field.active !== false)
          .map((field) => ({
            key: field.name,
            label: t(field.name),
            code: field.code,
          }))
      : allEnergyFields;

  const handleFieldToggle = (fieldKey, checked) => {
    setSelectedFields(prev => ({
      ...prev,
      [fieldKey]: checked
    }));

    // Si se selecciona, inicializar con la unidad actual
    if (checked && !fieldUnits[fieldKey]) {
      setFieldUnits(prev => ({
        ...prev,
        [fieldKey]: currentUnits?.[fieldKey] || ""
      }));
    }
  };

  const handleUnitChange = (fieldKey, unit) => {
    setFieldUnits(prev => ({
      ...prev,
      [fieldKey]: unit
    }));
  };

  const handleSelectAll = () => {
    const allSelected = {};
    const allUnits = {};
    energyFields.forEach(field => {
      allSelected[field.key] = true;
      allUnits[field.key] = currentUnits?.[field.key] || "";
    });
    setSelectedFields(allSelected);
    setFieldUnits(allUnits);
  };

  const handleDeselectAll = () => {
    setSelectedFields({});
  };

  const handleApply = () => {
    // Filtrar solo los campos seleccionados con sus unidades
    const unitsToApply = {};
    Object.keys(selectedFields).forEach(key => {
      if (selectedFields[key] && fieldUnits[key]) {
        unitsToApply[key] = fieldUnits[key];
      }
    });

    if (Object.keys(unitsToApply).length === 0) {
      return;
    }

    onApply(unitsToApply);
  };

  const selectedCount = Object.values(selectedFields).filter(Boolean).length;

  return (
    <Modal open={show} onClose={onClose} size="large" className="update-units-modal">
      {/* <Modal.Header>
        <Icon name="edit" />
        {t("update_units_in_forms") || "Modificar unidades en formularios existentes"}
      </Modal.Header> */}
      <Modal.Content scrolling>
        <div className="update-units-modal__title">{t("update_units_in_forms") || "Modificar unidades"}</div>
        {/* <Message info>
          <Message.Header>{t("select_fields_to_update") || "Selecciona los campos a modificar"}</Message.Header>
          <p>
            {t("update_units_description") ||
              "Selecciona los campos y las unidades que deseas aplicar a todos los formularios existentes del año."}
          </p>
        </Message> */}

        <div style={{ marginBottom: "15px" }}>
          <Button size="small" className="btn-select-all" onClick={handleSelectAll}>
            <Icon name="check square" /> {t("select_all") || "Seleccionar todos"}
          </Button>
          <Button size="small" className="btn-deselect-all" onClick={handleDeselectAll}>
            <Icon name="square outline" /> {t("deselect_all") || "Deseleccionar todos"}
          </Button>
          <span style={{ marginLeft: "15px", color: "#666" }}>
            {selectedCount} {t("fields_selected") || "campos seleccionados"}
          </span>
        </div>

        <Form>
          <div className="update-units-grid">
            {energyFields.map((field) => (
              <div key={field.key} className="update-unit-row">
                <Checkbox
                  checked={selectedFields[field.key] || false}
                  onChange={(_, data) => handleFieldToggle(field.key, data.checked)}
                  label={field.label}
                  className="field-checkbox"
                />
                {selectedFields[field.key] && (
                  <Form.Dropdown
                    placeholder={t("select_unit")}
                    options={determineEnergyUnits(field.key).map((unit) => ({
                      key: unit.key,
                      text: t(unit.name),
                      value: unit.value,
                    }))}
                    selection
                    search
                    compact
                    value={fieldUnits[field.key] || ""}
                    onChange={(_, data) => handleUnitChange(field.key, data.value)}
                    className="unit-dropdown"
                  />
                )}
              </div>
            ))}
          </div>
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button
          primary
          onClick={handleApply}
          disabled={selectedCount === 0 || isLoading}
          loading={isLoading}
        >
          <Icon name="save" />
          {t("apply_to_forms") || `Aplicar a formularios (${selectedCount})`}
        </Button>
        <Button secondary onClick={onClose} disabled={isLoading}>
          {t("cancel")}
        </Button>
      </Modal.Actions>
    </Modal>
  );
}
